import styled from "@emotion/styled";
import { LocalDateTime } from "@js-joda/core";
import { LayoutGroup } from "framer-motion";
import React from "react";
import { ClockInOutCallType, GeoAndAddress } from "../../messages/Visit";
import { STRINGS } from "../../strings";
import { globalize, translate } from "../../translate";
import { AppButton } from "../Generic/AppButton";
import { AppAbstractModal } from "../Generic/AppModal/AppAbstractModal";
import ModalActions from "../Generic/AppModal/components/ModalActions/ModalActions";
import ModalHeadline from "../Generic/AppModal/components/ModalHeadline/ModalHeadline";
import NotesText from "../Patient/components/NotesText";
import { default as TimeInRangeIcon } from "/public/assets/calendar-check-fill-icon.svg?react";
import { default as TimeNotInRangeIcon } from "/public/assets/calendar-warning-icon.svg?react";
import { default as LocationInRangeIcon } from "/public/assets/location-check-solid-icon.svg?react";
import { default as LocationNotInRangeIcon } from "/public/assets/location-warning-icon.svg?react";
import { default as ClockHeaderIcon } from "/public/assets/undraw_time_management_re_tk5w.svg?react";

export interface PromptClockReadyMedflytAgencyModalProps {
  isOpen: boolean;
  clockType: ClockInOutCallType;
  geoAndAddress: GeoAndAddress;
  clockAddressInRange: boolean;
  clockInTimeInRange: boolean;
  isCalibrating: boolean;
  onRequestCalibrate: () => void;
  onRequestClose: () => void;
  onClickClock: () => void;
}

const PromptClockReadyMedflytAgencyModal = (props: PromptClockReadyMedflytAgencyModalProps) => {
  const clockButtonText = (() => {
    switch (props.clockType) {
      case ClockInOutCallType.IN:
        return translate(STRINGS.READY_TO_CLOCK_IN);
      case ClockInOutCallType.OUT:
        return translate(STRINGS.READY_TO_CLOCK_OUT);
    }
  })();

  const clockTimeText = (() => {
    return props.clockInTimeInRange
      ? translate(STRINGS.CLOCK_TIME_IN_RANGE).replace(
          "{CLOCK_TYPE}",
          props.clockType.toLocaleLowerCase()
        )
      : translate(STRINGS.CLOCK_TIME_NOT_IN_RANGE).replace(
          "{CLOCK_TYPE}",
          props.clockType.toLocaleLowerCase()
        );
  })();

  const clockAddressText = (() => {
    return props.clockAddressInRange
      ? translate(STRINGS.CLOCK_ADDRESS_IN_RANGE).replace(
          "{CLOCK_TYPE}",
          props.clockType.toLocaleLowerCase()
        )
      : translate(STRINGS.CLOCK_ADDRESS_NOT_IN_RANGE).replace(
          "{CLOCK_TYPE}",
          props.clockType.toLocaleLowerCase()
        );
  })();

  return (
    <PromptClockReadyMedflytAgencyModal.Modal
      isOpen={props.isOpen}
      onRequestClose={props.onRequestClose}
    >
      <PromptClockReadyMedflytAgencyModal.ClockHeaderIconWrapper>
        <ClockHeaderIcon />
      </PromptClockReadyMedflytAgencyModal.ClockHeaderIconWrapper>
      <PromptClockReadyMedflytAgencyModal.ContentWrapper>
        <PromptClockReadyMedflytAgencyModal.MainHeader>
          {
            {
              IN: translate(STRINGS.LETS_START_YOUR_SHIFT),
              OUT: translate(STRINGS.LETS_FINISH_YOUR_SHIFT),
            }[props.clockType]
          }
        </PromptClockReadyMedflytAgencyModal.MainHeader>
        <PromptClockReadyMedflytAgencyModal.ClockDataWrapper>
          <ClockIcon
            type="Time"
            isInRange={props.clockType === "IN" ? props.clockInTimeInRange : true}
          />
          <PromptClockReadyMedflytAgencyModal.ClockDataText>
            <div>{clockTimeText}</div>
            <NotesText>
              {globalize().formatLocalDateTime(LocalDateTime.now(), { datetime: "short" })}
            </NotesText>
          </PromptClockReadyMedflytAgencyModal.ClockDataText>
        </PromptClockReadyMedflytAgencyModal.ClockDataWrapper>
        <PromptClockReadyMedflytAgencyModal.ClockDataWrapper>
          <ClockIcon type="Address" isInRange={props.clockAddressInRange} />
          <PromptClockReadyMedflytAgencyModal.ClockDataText>
            <div>{clockAddressText}</div>
          </PromptClockReadyMedflytAgencyModal.ClockDataText>
        </PromptClockReadyMedflytAgencyModal.ClockDataWrapper>
        <PromptClockReadyMedflytAgencyModal.ModalActions>
          {!props.clockAddressInRange && (
            <AppButton
              variant="filled"
              color="primary"
              onClick={props.onRequestCalibrate}
              isLoading={props.isCalibrating}
            >
              {translate(STRINGS.CHECK_AGAIN)}
            </AppButton>
          )}

          <LayoutGroup>
            <AppButton
              variant={props.clockAddressInRange ? "filled" : "soft"}
              color="primary"
              onClick={props.onClickClock}
            >
              {clockButtonText}
            </AppButton>
            <AppButton variant="link" color="black" onClick={props.onRequestClose}>
              {translate(STRINGS.NO_THANKS)}
            </AppButton>
          </LayoutGroup>
        </PromptClockReadyMedflytAgencyModal.ModalActions>
      </PromptClockReadyMedflytAgencyModal.ContentWrapper>
    </PromptClockReadyMedflytAgencyModal.Modal>
  );
};

export default PromptClockReadyMedflytAgencyModal;

const ClockIcon = (props: { type: "Address" | "Time"; isInRange: boolean }) => {
  switch (props.type) {
    case "Address":
      return props.isInRange ? (
        <PromptClockReadyMedflytAgencyModal.InRangeIcon>
          <LocationInRangeIcon />
        </PromptClockReadyMedflytAgencyModal.InRangeIcon>
      ) : (
        <PromptClockReadyMedflytAgencyModal.WarningIcon>
          <LocationNotInRangeIcon />
        </PromptClockReadyMedflytAgencyModal.WarningIcon>
      );
    case "Time":
      return props.isInRange ? (
        <PromptClockReadyMedflytAgencyModal.InRangeIcon>
          <TimeInRangeIcon />
        </PromptClockReadyMedflytAgencyModal.InRangeIcon>
      ) : (
        <PromptClockReadyMedflytAgencyModal.WarningIcon>
          <TimeNotInRangeIcon />
        </PromptClockReadyMedflytAgencyModal.WarningIcon>
      );
    default:
      console.error("Invalid type for ClockIcon");
      return <></>;
  }
};

PromptClockReadyMedflytAgencyModal.Modal = styled(AppAbstractModal)`
  .container {
    background-color: #fff;
    border-radius: 24px;
    box-sizing: border-box;
    display: flex;
    flex-direction: column;
    align-items: stretch;
    text-align: center;
    max-height: 90vh;
  }
`;

PromptClockReadyMedflytAgencyModal.ClockHeaderIconWrapper = styled.div`
  margin: 20px 0 0 75px;
  svg {
    display: block;
    width: 180px;
    height: 180px;
  }
  @media (max-height: 620px) {
    display: none;
  }
`;

PromptClockReadyMedflytAgencyModal.ContentWrapper = styled.div`
  padding: 0 32px 48px 32px;
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

PromptClockReadyMedflytAgencyModal.MainHeader = styled(ModalHeadline)`
  margin-bottom: 0 32px 32px 32px;
`;

PromptClockReadyMedflytAgencyModal.ClockDataWrapper = styled.div`
  margin-bottom: 20px;
  max-height: 80px;
  max-width: 400px;
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 20px;
`;

PromptClockReadyMedflytAgencyModal.ClockDataText = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  justify-content: flex-start;
  flex-wrap: nowrap;
  text-align: start;
`;

PromptClockReadyMedflytAgencyModal.InRangeIcon = styled.div`
  svg {
    color: #8fd14f;
    min-width: 30px !important;
    height: 30px !important;
  }
`;

PromptClockReadyMedflytAgencyModal.WarningIcon = styled.div`
  svg {
    color: #f6c32e;
    min-width: 30px !important;
    height: 30px !important;
  }
`;

PromptClockReadyMedflytAgencyModal.ModalActions = styled(ModalActions)`
  display: flex;
  flex-direction: column;
  align-items: stretch;
  margin-bottom: -24px;
`;
